import { apiClient } from './client';

export type SurveyOption = string | { label: string; value: string };

export interface SurveyRatingConfig {
    min: number;
    max: number;
    step: number;
    left_label?: string;
    right_label?: string;
    value_labels?: Record<string, string>;
}

export interface SurveyQuestionConfig {
    placeholder?: string;
    help_text?: string;
    allow_other?: boolean;
    rating?: SurveyRatingConfig;
    [key: string]: unknown;
}

export interface SurveyQuestion {
    id?: string;
    field_name: string;
    required: boolean;
    field_type: string;
    options?: SurveyOption[];
    field_label?: string;
    validation?: SurveyQuestionValidation;
    config?: SurveyQuestionConfig;
}

export type SurveyValidationStyle =
    | 'text'
    | 'email'
    | 'phone'
    | 'cpf'
    | 'date'
    | 'number'
    | 'select'
    | 'multiselect';

export type SurveyValidationInputMode = 'text' | 'email' | 'tel' | 'numeric';

export type SurveyValidationNormalizeRule = 'trim' | 'lowercase' | 'digits_only';

export interface SurveyQuestionValidation {
    style: SurveyValidationStyle;
    input_mode?: SurveyValidationInputMode;
    mask?: string;
    regex?: string;
    message?: string;
    normalize?: SurveyValidationNormalizeRule[];
}

export interface SurveySection {
    id: string;
    title: string;
    description: string;
    questions: SurveyQuestion[];
}

export interface Survey {
    id: string;
    name: string;
    instructions?: string;
    source?: 'platform' | 'custom';
    sections: SurveySection[];
    answers?: Record<string, unknown>;
    status?: 'DRAFT' | 'PUBLISHED' | string;
}

export interface SurveyReport {
    id: string;
    patientId: string;
    patientName: string;
    avatarUrl?: string | null;
    surveyName: string;
    surveySource?: 'platform' | 'custom';
    surveyVersionId?: string | null;
    status: string;
    sentAt: string;
    answers?: Record<string, unknown>;
}

interface SurveyReportDetail extends SurveyReport {
    survey: Survey;
}

export interface CustomSurveySnapshot {
    name: string;
    instructions?: string;
    sections: SurveySection[];
}

export interface CustomSurveyVersionSummary {
    id: string;
    version_number: number;
    status: 'DRAFT' | 'PUBLISHED';
    schema: CustomSurveySnapshot;
    published_at: string | null;
    updated_at: string;
}

export interface CustomSurveySummary {
    id: string;
    name: string;
    source: 'custom';
    draft: CustomSurveyVersionSummary | null;
    published: CustomSurveyVersionSummary | null;
    pendingAssignments?: number;
    completedAssignments?: number;
    updated_at: string;
}

export interface GoogleFormSkippedItem {
    title: string;
    reason: string;
    typeLabel: string;
}

export interface GoogleFormImportResult {
    snapshot: CustomSurveySnapshot;
    skipped: GoogleFormSkippedItem[];
    hasBranching: boolean;
}

interface CustomSurveyDeleteImpact {
    pendingAssignmentsExpired: number;
    completedResponsesKept: number;
}

export interface IntakeLinkData {
    id: string;
    psychologist_id: string;
    survey_id: string;
    status: string;
    hook: string;
    unlock_token: string;
    created_at: string;
    unlock_token_expires_at: string;
}

export interface IntakeLinkResponse {
    data: IntakeLinkData;
}

/**
 * Loosely-typed response for send/getAssignmentToken. The apiClient response
 * interceptor unwraps `response.data`, but some callers still read `.data`, so
 * both the wrapped and unwrapped token fields are optional here.
 */
interface AssignmentSendResponse {
    unlock_token?: string;
    token?: string;
    unlock_token_expires_at?: string;
    data?: {
        unlock_token?: string;
        token?: string;
        unlock_token_expires_at?: string;
    };
}

export const surveysApi = {
    list: async (): Promise<Survey[]> => {
        const response = await apiClient.get<Survey[]>('/surveys');
        // Casting because the interceptor unwraps the response, so 'response' is the body (Survey[]).
        // But TypeScript expects AxiosResponse.
        return response as unknown as Survey[];
    },

    listReports: async (): Promise<SurveyReport[]> => {
        const response = await apiClient.get<SurveyReport[]>('/surveys/reports');
        return response as unknown as SurveyReport[];
    },

    getReport: async (reportId: string): Promise<SurveyReportDetail> => {
        const response = await apiClient.get<{ data: SurveyReportDetail }>(
            `/surveys/reports/${reportId}`,
        );
        return (response as unknown as { data: SurveyReportDetail }).data;
    },

    listCustom: async (): Promise<CustomSurveySummary[]> => {
        const response = await apiClient.get<{ data: CustomSurveySummary[] }>('/surveys/custom');
        return (response as unknown as { data: CustomSurveySummary[] }).data;
    },

    getCustom: async (surveyId: string): Promise<CustomSurveySummary> => {
        const response = await apiClient.get<{ data: CustomSurveySummary }>(
            `/surveys/custom/${surveyId}`,
        );
        return (response as unknown as { data: CustomSurveySummary }).data;
    },

    createCustom: async (snapshot: CustomSurveySnapshot): Promise<CustomSurveySummary> => {
        const response = await apiClient.post<{ data: CustomSurveySummary }>(
            '/surveys/custom',
            snapshot,
        );
        return (response as unknown as { data: CustomSurveySummary }).data;
    },

    importGoogleForm: async (url: string): Promise<GoogleFormImportResult> => {
        const response = await apiClient.post<{ data: GoogleFormImportResult }>(
            '/surveys/google-forms/import',
            { url },
        );
        return (response as unknown as { data: GoogleFormImportResult }).data;
    },

    saveCustomDraft: async (
        surveyId: string,
        snapshot: CustomSurveySnapshot,
    ): Promise<CustomSurveySummary> => {
        const response = await apiClient.put<{ data: CustomSurveySummary }>(
            `/surveys/custom/${surveyId}/draft`,
            snapshot,
        );
        return (response as unknown as { data: CustomSurveySummary }).data;
    },

    publishCustom: async (surveyId: string): Promise<CustomSurveySummary> => {
        const response = await apiClient.post<{ data: CustomSurveySummary }>(
            `/surveys/custom/${surveyId}/publish`,
            { acknowledged: true },
        );
        return (response as unknown as { data: CustomSurveySummary }).data;
    },

    deleteCustomDraft: async (surveyId: string): Promise<CustomSurveySummary> => {
        const response = await apiClient.delete<{ data: CustomSurveySummary }>(
            `/surveys/custom/${surveyId}/draft`,
        );
        return (response as unknown as { data: CustomSurveySummary }).data;
    },

    deleteCustom: async (surveyId: string): Promise<CustomSurveyDeleteImpact> => {
        const response = await apiClient.delete<{ data: CustomSurveyDeleteImpact }>(
            `/surveys/custom/${surveyId}`,
        );
        return (response as unknown as { data: CustomSurveyDeleteImpact }).data;
    },

    send: async (
        patientId: string,
        surveyId: string,
        confirm: boolean = false,
    ): Promise<AssignmentSendResponse> => {
        const payload: { surveyId: string; confirm?: boolean } = { surveyId };
        if (confirm) {
            payload.confirm = true;
        }
        const response = await apiClient.post(`/patients/${patientId}/surveys`, payload);
        return response;
    },

    getSurveyByToken: async (token: string): Promise<Survey> => {
        const response = await apiClient.get<{ data: Survey }>(
            `/patients/survey/answer?token=${token}`,
            { withCredentials: true },
        );
        return (response as unknown as { data: Survey }).data;
    },

    submitSurveyAnswer: async (
        token: string,
        data: { answers: Record<string, unknown>; status: string },
    ): Promise<void> => {
        await apiClient.post(`/patients/survey/answer?token=${token}`, data, {
            withCredentials: true,
        });
    },

    getAssignmentToken: async (
        patientId: string,
        assignmentId: string,
    ): Promise<AssignmentSendResponse> => {
        const response = await apiClient.get(
            `/patients/${patientId}/surveys/${assignmentId}/token`,
        );
        return response;
    },

    createIntakeLink: async (surveyId: string, alias: string): Promise<IntakeLinkResponse> => {
        const response = await apiClient.post<IntakeLinkResponse>('/surveys/intake-links', {
            surveyId,
            alias,
        });
        return response as unknown as IntakeLinkResponse;
    },
};
